import { ReactElement } from 'react';

import { RowData } from '../model';
import { AppTableProps } from '../model/appTableProps';

const AppTable = ({
  data,
  showAction,
  action = [],
  th1,
  th2,
  isNested = false,
  handleEditClick,
  showActionText,
}: Readonly<AppTableProps>) => {
  const mainData: RowData[] | undefined = isNested
    ? (data?.[0]?.permissions as RowData[] | undefined)
    : data;

  const getLabel = (row: RowData): string =>
    String(row?.description ?? row?.permissionName ?? row?.activityName ?? '');

  const getCode = (row: RowData): string =>
    String((row?.value as string)?.trim() ?? row?.permissionCode ?? row?.activityCode ?? '');

  const renderActions = (row: RowData) =>
    (action as ReactElement[]).map((iconElement: ReactElement) => {
      if (!iconElement.key) {
        throw new Error(
          'Each action element passed to AppTable must have a unique `key`.'
        );
      }

      return (
        <button
          key={iconElement.key}
          type="button"
          className="inline-block mx-1 cursor-pointer p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
          onClick={() => handleEditClick?.(row)}
          aria-label="Action"
        >
          {iconElement}
        </button>
      );
    });

  return (
    <div className="overflow-x-auto custom-scroll border border-gray-200 dark:border-gray-700 rounded-lg">
      <table className="w-full text-sm text-left border-separate border-spacing-0">
        <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-100 uppercase">
          <tr>
            <th className="px-4 py-2 border-b border-r border-gray-200 dark:border-gray-700 last:border-r-0">
              {th1 ?? 'Description'}
            </th>
            <th className="px-4 py-2 border-b border-r border-gray-200 dark:border-gray-700 last:border-r-0">
              {th2 ?? 'Value'}
            </th>
            {showAction && (
              <th className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-center">
                {showActionText}
              </th>
            )}
          </tr>
        </thead>
        <tbody className="text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-900">
          {mainData?.map((row: RowData, idx: number) => (
            <tr
              key={row.id != null ? String(row.id) : `row-${idx}`}
              className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <td className="px-4 py-2 border-b border-r border-gray-200 dark:border-gray-700 last:border-r-0 truncate max-w-[200px]" title={getLabel(row)}>
                {getLabel(row)}
              </td>
              <td className="px-4 py-2 border-b border-r border-gray-200 dark:border-gray-700 last:border-r-0 truncate max-w-[200px]" title={getCode(row)}>
                {getCode(row)}
              </td>
              {showAction && (
                <td className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-center">
                  {renderActions(row)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AppTable;
